// Audio conversion utilities for g711 u-law to WAV

// Audio buffer configuration
export const AUDIO_CONFIG = {
  BUFFER_SIZE: 4000, // bytes of ulaw audio to buffer before playing
  BUFFER_TIMEOUT_MS: 300, // Force flush buffer after this many ms
  PRE_BUFFER_CHUNKS: 3, // Wait for this many chunks before starting playback
  OUTPUT_SAMPLE_RATE: 16000, // Upsample to 16kHz for better quality
  GAIN: 5.0, // Volume boost factor for telephony audio
} as const;

// Standard ITU-T G.711 mu-law decoding table
const ULAW_DECODE_TABLE = new Int16Array([
  -32124, -31100, -30076, -29052, -28028, -27004, -25980, -24956,
  -23932, -22908, -21884, -20860, -19836, -18812, -17788, -16764,
  -15996, -15484, -14972, -14460, -13948, -13436, -12924, -12412,
  -11900, -11388, -10876, -10364, -9852, -9340, -8828, -8316,
  -7932, -7676, -7420, -7164, -6908, -6652, -6396, -6140,
  -5884, -5628, -5372, -5116, -4860, -4604, -4348, -4092,
  -3900, -3772, -3644, -3516, -3388, -3260, -3132, -3004,
  -2876, -2748, -2620, -2492, -2364, -2236, -2108, -1980,
  -1884, -1820, -1756, -1692, -1628, -1564, -1500, -1436,
  -1372, -1308, -1244, -1180, -1116, -1052, -988, -924,
  -876, -844, -812, -780, -748, -716, -684, -652,
  -620, -588, -556, -524, -492, -460, -428, -396,
  -372, -356, -340, -324, -308, -292, -276, -260,
  -244, -228, -212, -196, -180, -164, -148, -132,
  -120, -112, -104, -96, -88, -80, -72, -64,
  -56, -48, -40, -32, -24, -16, -8, 0,
  32124, 31100, 30076, 29052, 28028, 27004, 25980, 24956,
  23932, 22908, 21884, 20860, 19836, 18812, 17788, 16764,
  15996, 15484, 14972, 14460, 13948, 13436, 12924, 12412,
  11900, 11388, 10876, 10364, 9852, 9340, 8828, 8316,
  7932, 7676, 7420, 7164, 6908, 6652, 6396, 6140,
  5884, 5628, 5372, 5116, 4860, 4604, 4348, 4092,
  3900, 3772, 3644, 3516, 3388, 3260, 3132, 3004,
  2876, 2748, 2620, 2492, 2364, 2236, 2108, 1980,
  1884, 1820, 1756, 1692, 1628, 1564, 1500, 1436,
  1372, 1308, 1244, 1180, 1116, 1052, 988, 924,
  876, 844, 812, 780, 748, 716, 684, 652,
  620, 588, 556, 524, 492, 460, 428, 396,
  372, 356, 340, 324, 308, 292, 276, 260,
  244, 228, 212, 196, 180, 164, 148, 132,
  120, 112, 104, 96, 88, 80, 72, 64,
  56, 48, 40, 32, 24, 16, 8, 0,
]);

function writeString(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

function upsamplePcm(samples: Int16Array, inputRate: number, outputRate: number): Int16Array {
  if (inputRate >= outputRate) return samples;

  const ratio = outputRate / inputRate;
  const outputLength = Math.floor(samples.length * ratio);
  const output = new Int16Array(outputLength);

  for (let i = 0; i < outputLength; i++) {
    const srcPos = i / ratio;
    const srcIndex = Math.floor(srcPos);
    const frac = srcPos - srcIndex;

    if (srcIndex >= samples.length - 1) {
      output[i] = samples[samples.length - 1];
    } else {
      const sample1 = samples[srcIndex];
      const sample2 = samples[srcIndex + 1];
      output[i] = Math.round(sample1 + frac * (sample2 - sample1));
    }
  }

  return output;
}

export function decodeBase64ToBytes(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export function createWavFromUlawBytes(ulawBytes: Uint8Array, inputSampleRate = 8000): string {
  const { OUTPUT_SAMPLE_RATE, GAIN } = AUDIO_CONFIG;

  // Convert u-law to linear PCM with gain boost
  const pcmSamples = new Int16Array(ulawBytes.length);
  for (let i = 0; i < ulawBytes.length; i++) {
    const sample = ULAW_DECODE_TABLE[ulawBytes[i]] * GAIN;
    pcmSamples[i] = Math.max(-32768, Math.min(32767, Math.round(sample)));
  }

  // Upsample from 8kHz to 16kHz
  const upsampledPcm = upsamplePcm(pcmSamples, inputSampleRate, OUTPUT_SAMPLE_RATE);

  // WAV header constants
  const numChannels = 1;
  const bitsPerSample = 16;
  const byteRate = OUTPUT_SAMPLE_RATE * numChannels * (bitsPerSample / 8);
  const blockAlign = numChannels * (bitsPerSample / 8);
  const dataSize = upsampledPcm.length * 2;
  const headerSize = 44;
  const fileSize = headerSize + dataSize;

  const buffer = new ArrayBuffer(fileSize);
  const view = new DataView(buffer);

  // RIFF header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, fileSize - 8, true);
  writeString(view, 8, 'WAVE');

  // fmt chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, OUTPUT_SAMPLE_RATE, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);

  // data chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  // Write PCM samples
  let offset = 44;
  for (let i = 0; i < upsampledPcm.length; i++) {
    view.setInt16(offset, upsampledPcm[i], true);
    offset += 2;
  }

  // Convert to base64
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return 'data:audio/wav;base64,' + btoa(binary);
}
